import { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  makeStyles,
} from '@fluentui/react-components';
import { useInfiniteQuery } from '@tanstack/react-query';
import { RefreshCw, Search } from 'lucide-react';

import { extractConsentError, getTenantUsers, isGraphError } from '../../api/identity';
import { ApiError } from '../../api/client';
import { TenantRole, TenantUser, TenantUsersData } from '../../types/identity';
import { DataTable, DataTableColumn } from '../DataTable';
import TenantLabel from '../TenantLabel';
import ConsentMessageBar from './ConsentMessageBar';

// FD-381 — the tenant-user search, extracted from UserPickerDialog so the same
// search can appear twice side by side in the add flow and once inside the
// row-level Change dialog.
//
// THE HEADER IS THE POINT. Before this the only tenant signal anywhere in the
// picker was the literal word "source" or "destination" in a hint line —
// `TenantLabel` was not used and neither the tenant domain nor its GUID
// appeared, on a screen whose entire purpose is choosing between two tenants.
// An operator running several engagements had no way to tell, from the dialog,
// which directory they were about to search.
//
// The tenant identity is resolved from two sources, preferring the caller's:
// the migration record carries `sourceTenantDomain`/`destTenantDomain` (FD-110
// resolution), and the `tenant-users` response itself carries the `tenantId`
// the server resolved for the role. The second matters for the D2C destination,
// where the migration row legitimately has no `DestTenantID` and the server
// falls back to `DEPLOYMENT_TENANT_ID` (FD-ADR-029) — so the panel can still
// name what it searched instead of showing nothing.

const MIN_SEARCH_LENGTH = 3;
const DEBOUNCE_MS = 300;

const useStyles = makeStyles({
  panel: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '10px',
    minWidth: '0',
  },
  head: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '2px',
  },
  role: {
    fontSize: '11px',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: '#71717a',
  },
  hint: {
    fontSize: '12px',
    color: '#52525b',
  },
  results: {
    borderTopWidth: '1px',
    borderRightWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '1px',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: '#e4e4e7',
    borderRightColor: '#e4e4e7',
    borderBottomColor: '#e4e4e7',
    borderLeftColor: '#e4e4e7',
    borderTopLeftRadius: '6px',
    borderTopRightRadius: '6px',
    borderBottomLeftRadius: '6px',
    borderBottomRightRadius: '6px',
    // Painted white rather than left transparent (owner, 2026-09-28). Mounted
    // inside the inspector this box inherits whatever fill is behind it, so the
    // results carried the same ground as the card around them and the list read
    // as part of the search box instead of as the answers to it. On a page with
    // a white body this changes nothing, which is why it went unnoticed.
    backgroundColor: '#ffffff',
    maxHeight: '320px',
    overflowY: 'auto',
  },
  loadMoreRow: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: '8px',
  },
  listRow: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    columnGap: '8px',
    width: '100%',
    paddingTop: '6px',
    paddingBottom: '6px',
    paddingLeft: '10px',
    paddingRight: '10px',
    borderTopWidth: '0',
    borderRightWidth: '0',
    borderLeftWidth: '0',
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: '#f4f4f5',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    textAlign: 'left',
    ':hover': { backgroundColor: '#f4f8fd' },
    ':last-child': { borderBottomWidth: '0' },
  },
});

export interface TenantUserSearchPanelProps {
  migrationId: string;
  tenant: TenantRole;
  /** Operator-facing tenant domain from the migration record, when known. */
  tenantDomain?: string | null;
  /** Tenant GUID from the migration record, when known. */
  tenantId?: string | null;
  /** Highlighted row, so a two-panel flow can show what is already chosen. */
  selectedUserId?: string | null;
  onSelect: (user: TenantUser) => void;
  /** Set false to stop searching while the panel is not in use. */
  enabled?: boolean;
  /**
   * FD-517 — how RESULTS are presented. The query, the debounce, the tenant
   * header, the consent handling and the paging are identical in both; only
   * this differs, because a second search component is how two searches come to
   * disagree about which tenant they are in.
   *
   * `table` is the five-column grid the two-panel add flow uses, where there is
   * real width. `list` is a vertical name-over-UPN list for the inspector,
   * where a five-column table in a 560px overlay wraps every cell and scrolls
   * sideways inside a panel that is already an overlay.
   */
  variant?: 'table' | 'list';
}

export default function TenantUserSearchPanel({
  migrationId,
  tenant,
  tenantDomain,
  tenantId,
  selectedUserId,
  onSelect,
  enabled = true,
  variant = 'table',
}: TenantUserSearchPanelProps) {
  const styles = useStyles();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const handle = window.setTimeout(() => setSearch(searchInput.trim()), DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  const searchReady = search.length >= MIN_SEARCH_LENGTH;

  const query = useInfiniteQuery<TenantUsersData, Error>({
    queryKey: ['tenant-users', migrationId, tenant, search],
    queryFn: ({ pageParam }) =>
      getTenantUsers(migrationId, {
        tenant,
        search,
        skipToken: (pageParam as string | null | undefined) ?? undefined,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.skipToken ?? undefined,
    enabled: enabled && searchReady,
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.status === 409) return false;
      return failureCount < 1;
    },
  });

  const users = useMemo<TenantUser[]>(
    () => (query.data ? query.data.pages.flatMap((p) => p.users) : []),
    [query.data],
  );

  // Prefer the caller's value; fall back to what the server said it searched.
  const resolvedTenantId = tenantId ?? query.data?.pages[0]?.tenantId ?? null;

  const columns = useMemo<DataTableColumn<TenantUser>[]>(
    () => [
      {
        key: 'displayName',
        header: 'Name',
        render: (u) => (
          <span className="font-medium text-zinc-900">{u.displayName ?? '—'}</span>
        ),
      },
      {
        key: 'userPrincipalName',
        header: 'UPN',
        numeric: true,
        render: (u) => <span>{u.userPrincipalName ?? '—'}</span>,
      },
      {
        key: 'mail',
        header: 'Mail',
        render: (u) => <span>{u.mail ?? '—'}</span>,
      },
      {
        key: 'onPremisesSamAccountName',
        header: 'SAM',
        numeric: true,
        render: (u) => <span>{u.onPremisesSamAccountName ?? '—'}</span>,
      },
      {
        key: 'accountEnabled',
        header: 'Account',
        render: (u) =>
          u.accountEnabled ? (
            <Badge appearance="tint" color="success">
              Enabled
            </Badge>
          ) : (
            <Badge appearance="tint" color="subtle">
              Disabled
            </Badge>
          ),
      },
    ],
    [],
  );

  const consent = query.isError ? extractConsentError(query.error) : null;

  const errorNode = query.isError ? (
    consent ? (
      <ConsentMessageBar consent={consent} />
    ) : (
      <MessageBar intent="error">
        <MessageBarBody>
          <MessageBarTitle>
            {isGraphError(query.error)
              ? 'Directory temporarily unavailable'
              : 'Could not search users'}
          </MessageBarTitle>
          {isGraphError(query.error)
            ? 'Microsoft Graph did not respond. This is usually transient — retry in a moment.'
            : errorMessage(query.error)}
        </MessageBarBody>
        <Button
          size="small"
          appearance="subtle"
          icon={<RefreshCw size={14} strokeWidth={1.5} />}
          onClick={() => query.refetch()}
        >
          Retry
        </Button>
      </MessageBar>
    )
  ) : undefined;

  const roleWord = tenant === 'source' ? 'Source tenant' : 'Destination tenant';

  return (
    <div className={styles.panel}>
      <div className={styles.head}>
        <span className={styles.role}>{roleWord}</span>
        <TenantLabel
          domain={tenantDomain}
          tenantId={resolvedTenantId}
          fallback="Tenant not yet resolved"
        />
      </div>

      <Field>
        <Input
          value={searchInput}
          onChange={(_, data) => setSearchInput(data.value)}
          placeholder="Search by name, UPN, or mail"
          contentBefore={<Search size={14} strokeWidth={1.5} />}
          aria-label={`Search ${tenant} tenant users`}
        />
      </Field>

      {!searchReady ? (
        <div className={styles.hint}>
          Type at least {MIN_SEARCH_LENGTH} characters to search this tenant.
        </div>
      ) : (
        <>
          <div className={styles.results}>
            {variant === 'list' ? (
              <ResultList
                users={users}
                search={search}
                isLoading={query.isLoading}
                error={errorNode}
                selectedUserId={selectedUserId}
                onSelect={onSelect}
                rowClassName={styles.listRow}
                hintClassName={styles.hint}
              />
            ) : (
              <DataTable
                columns={columns}
                data={users}
                keyForRow={(u) => u.id}
                rowDomId={(u) => `tenant-user-${u.id}`}
                onRowClick={(u) => onSelect(u)}
                isLoading={query.isLoading}
                loadingRowCount={4}
                error={errorNode}
                emptyState={
                  <span className={styles.hint}>
                    No users match “{search}” in this tenant.
                  </span>
                }
              />
            )}
          </div>
          {query.hasNextPage && !query.isError && (
            <div className={styles.loadMoreRow}>
              <Button
                appearance="secondary"
                size="small"
                onClick={() => query.fetchNextPage()}
                disabled={query.isFetchingNextPage}
              >
                {query.isFetchingNextPage ? 'Loading…' : 'Load more'}
              </Button>
            </div>
          )}
          {selectedUserId && (
            <div className={styles.hint} data-testid={`selected-${tenant}`}>
              Selected:{' '}
              {users.find((u) => u.id === selectedUserId)?.displayName ?? selectedUserId}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * The `list` presentation. A real `<button>` per row, so keyboard reach comes
 * from the element rather than from a handler: the table variant gets that from
 * `DataTable`, and a clickable `<div>` here would quietly lose it.
 */
function ResultList({
  users,
  search,
  isLoading,
  error,
  selectedUserId,
  onSelect,
  rowClassName,
  hintClassName,
}: {
  users: TenantUser[];
  search: string;
  isLoading: boolean;
  error?: React.ReactNode;
  selectedUserId?: string | null;
  onSelect: (user: TenantUser) => void;
  rowClassName: string;
  hintClassName: string;
}) {
  if (error) return <div className="p-2">{error}</div>;
  if (isLoading) {
    return (
      <div className={`${hintClassName} p-3`}>Searching…</div>
    );
  }
  if (users.length === 0) {
    return (
      <div className={`${hintClassName} p-3`}>
        No users match “{search}” in this tenant.
      </div>
    );
  }
  // `<ul>`/`<li>` with the button INSIDE, never `role="listitem"` on the button
  // itself: an explicit role REPLACES the implicit one, so a button carrying
  // `role="listitem"` stops being a button to assistive tech — and stops being
  // reachable by `getByRole('button')`, which is how this was caught.
  return (
    <ul className="m-0 list-none p-0">
      {users.map((u) => (
        <li key={u.id}>
        <button
          type="button"
          id={`tenant-user-${u.id}`}
          className={rowClassName}
          aria-current={selectedUserId === u.id ? 'true' : undefined}
          onClick={() => onSelect(u)}
        >
          <span className="flex min-w-0 flex-col">
            <span className="truncate text-[13px] font-medium text-zinc-900">
              {u.displayName ?? u.userPrincipalName ?? u.id}
            </span>
            {u.userPrincipalName && (
              <span className="truncate text-[12px] text-zinc-500">
                {u.userPrincipalName}
              </span>
            )}
          </span>
          {/* Disabled is the only account state worth a badge here: an ENABLED
              badge on every row is noise, and a disabled destination is the
              FD-511 exception that silently suppresses the sweep. */}
          {u.accountEnabled === false && (
            <Badge appearance="tint" color="warning">
              Disabled
            </Badge>
          )}
        </button>
        </li>
      ))}
    </ul>
  );
}

function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  return 'Unexpected error';
}
